// Tiny inline SVG sparkline — zero deps, ~500 bytes rendered. Used
// across /admin dashboards to show 24h trends without pulling in
// Recharts. If we ever need axes/legends/tooltips, upgrade to a real
// chart lib in that one place; sparklines stay as-is.

interface Props {
    data:    number[];
    width?:  number;
    height?: number;
    color?:  string;
    fill?:   string;
    strokeWidth?: number;
}

export function Sparkline({ data, width = 96, height = 20, color = '#8b1919', fill, strokeWidth = 1.5 }: Props) {
    if (data.length === 0) return <svg width={width} height={height} />;

    const max = Math.max(...data, 1);
    const min = Math.min(...data);
    const range = max - min || 1;
    const step = data.length > 1 ? width / (data.length - 1) : 0;

    const points = data.map((v, i) => {
        const x = i * step;
        const y = height - ((v - min) / range) * (height - 2) - 1;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');

    // Optional filled area underneath
    const areaPath = fill
        ? `M0,${height} L${points.replace(/ /g, ' L')} L${width},${height} Z`
        : null;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
             style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            {areaPath && <path d={areaPath} fill={fill} opacity={0.15} />}
            <polyline points={points} fill="none" stroke={color} strokeWidth={strokeWidth}
                      strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

// BarSparkline — same footprint but rendered as vertical bars. Better for
// discrete hourly buckets (signal counts, action counts) where continuity
// doesn't matter.
export function BarSparkline({ data, width = 96, height = 20, color = '#8b1919' }: Props) {
    if (data.length === 0) return <svg width={width} height={height} />;
    const max = Math.max(...data, 1);
    const bw = width / data.length;
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
             style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            {data.map((v, i) => {
                const h = Math.max(1, (v / max) * (height - 1));
                return (
                    <rect key={i} x={i * bw + 0.5} y={height - h} width={bw - 1} height={h} fill={color} />
                );
            })}
        </svg>
    );
}
